import React, { useCallback } from "react";
import dayjs from "dayjs";
import {
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getTribunalDisplayName } from "./boardUtils";

const DAYS = ["dom.", "lun.", "mar.", "mié.", "jue.", "vie.", "sáb."];

const FORMA_PAGO = {
  "1": "Efectivo",
  "2": "Transferencia",
  "3": "Cheque",
  "4": "Orden de Pago",
};

function fmtDate(ev) {
  const s = dayjs(ev.start);
  return `${DAYS[s.day()]} ${s.format("DD/MM/YYYY")}`;
}

function fmtTime(ev) {
  const s = dayjs(ev.start);
  const e = dayjs(ev.end || ev.start);
  return `${s.format("HH:mm")} - ${e.format("HH:mm")}`;
}

function fmtMonto(raw) {
  if (!raw) return "—";
  const n = parseFloat(raw);
  if (isNaN(n)) return String(raw);
  return "$" + n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Label del header siempre basado en el título, no en source_table.
// source_table solo decide si mostrar la grilla del body (ver isPagoConvenio).
function parseEventTitle(title) {
  if (!title) return { typeLabel: "", namesLabel: "(Sin título)" };

  // "Pago de convenio (nombre vs empresa) [subsecuente #N] [monto]"
  if (/^pago\s+de\s+convenio/i.test(title)) {
    const parenMatch = title.match(/\(([^)]+)\)/);
    let names;
    if (parenMatch) {
      names = parenMatch[1].trim().toUpperCase();
    } else {
      names = title
        .replace(/^pago\s+de\s+convenio\s*/i, "")
        .replace(/\s+subsecuente\s+#?\d+.*/i, "")
        .replace(/\s+[\d.]+$/, "")
        .trim()
        .toUpperCase();
    }
    const numMatch = title.match(/subsecuente\s+#?\s*(\d+)/i) || title.match(/#\s*(\d+)/);
    const num = numMatch ? ` #${numMatch[1]}` : "";
    return { typeLabel: `PAGO DE CONVENIO${num}`, namesLabel: names };
  }

  // "[2da] AUD. PREJ. Nombre vs Razón social" o "[2da] Audiencia [Prejudicial] Nombre vs Razón social"
  // El número/ordinal y "AUD. PREJ." suben juntos al label; namesLabel se queda solo con solicitante vs razón social.
  const audPrejMatch = title.match(
    /^(\d+\s*(?:ra|da|ta|er|to|va|o|a)?\.?\s*)?(aud\.?\s*prej\.?|audiencia\s*prejudicial|audiencia\s*preliminar|audiencia)\s+(.+)/i
  );
  if (audPrejMatch) {
    const ordinal = (audPrejMatch[1] || "").trim();
    const keyword = audPrejMatch[2].trim();
    return {
      typeLabel: `${ordinal} ${keyword}`.trim().toUpperCase(),
      namesLabel: audPrejMatch[3].trim().toUpperCase(),
    };
  }

  // "Audiencia X" o "Próxima audiencia X" — todo lo que precede + "audiencia" es el label
  const audMatch = title.match(/^(.{0,30}?audiencia)\s+(.+)/i);
  if (audMatch) {
    return {
      typeLabel: audMatch[1].trim().toUpperCase(),
      namesLabel: audMatch[2].trim().toUpperCase(),
    };
  }

  // Fallback genérico: primera palabra como label
  const spaceIdx = title.indexOf(" ");
  if (spaceIdx > 0) {
    return {
      typeLabel: title.slice(0, spaceIdx).toUpperCase(),
      namesLabel: title.slice(spaceIdx + 1).toUpperCase(),
    };
  }
  return { typeLabel: "", namesLabel: title.toUpperCase() };
}

function parseConvenioDesc(desc) {
  if (!desc) return {};
  const plainText = desc.replace(/<[^>]+>/g, "");
  const monto = plainText.match(/Monto:\s*([\d.]+)/i)?.[1] ?? null;
  const formaId = plainText.match(/Forma:\s*(\d+)/i)?.[1] ?? null;
  const expediente = plainText.match(/Expediente:\s*([A-Z0-9/]+)/i)?.[1] ?? null;

  // Convenios de tribunal no capturan "Forma de pago" (efectivo/transferencia/etc, eso solo
  // existe en conciliación); en su lugar usan "Tipo de pago" (Pago único / Parcialidades),
  // elegido al guardar la terminación en TabTerminacion. Registros viejos no traen la línea
  // explícita "Tipo de pago:", así que se infiere de la 1a línea de la descripción.
  const tipoPagoMatch = plainText.match(/Tipo de pago:\s*([^.\n]+)/i);
  let tipoPago = tipoPagoMatch ? tipoPagoMatch[1].trim() : null;
  if (!tipoPago) {
    if (/pago\s+único\s+del\s+convenio|pago\s+unico\s+del\s+convenio/i.test(plainText)) {
      tipoPago = "Pago único";
    } else if (/pago\s+parcialidad/i.test(plainText)) {
      tipoPago = "Parcialidades";
    }
  }

  return {
    monto,
    forma: formaId ? (FORMA_PAGO[formaId] ?? `Forma ${formaId}`) : null,
    tipoPago,
    expediente,
  };
}

const S = {
  card: {
    background: "#fff",
    borderRadius: 20,
    overflow: "hidden",
    fontFamily: "var(--app-font-sans)",
  },
  header: {
    background: "#2b558c",
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  typeLabel: {
    color: "rgba(255,255,255,0.65)",
    fontSize: "0.62rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    marginBottom: 3,
  },
  title: {
    color: "white",
    fontWeight: 700,
    fontSize: 15,
    margin: 0,
    lineHeight: 1.35,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  btnEdit: {
    background: "#2b77d2",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "7px 9px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    flexShrink: 0,
    lineHeight: 1,
  },
  btnDelete: {
    background: "#d83f3f",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "7px 9px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    flexShrink: 0,
    lineHeight: 1,
  },
  // Info bar: grid 1fr 1fr 1.8fr — UBICACIÓN gets the most space
  infoBar: {
    background: "#f9fafb",
    borderBottom: "1px solid #d1d5db",
    padding: "10px 20px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1.8fr",
    alignItems: "start",
  },
  infoCol: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    paddingRight: 8,
  },
  infoColBorder: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    borderLeft: "1px solid #d1d5db",
    paddingLeft: 14,
    paddingRight: 8,
  },
  iconBox: {
    background: "white",
    border: "1px solid #e5e7eb",
    padding: "6px 7px",
    borderRadius: 8,
    color: "#4b5563",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    alignSelf: "flex-start",
    marginTop: 1,
  },
  iconPlain: {
    color: "#4b5563",
    fontSize: 18,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    alignSelf: "flex-start",
    marginTop: 1,
  },
  infoLabel: {
    fontSize: "0.6rem",
    color: "#828a96",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 2,
  },
  infoValue: {
    color: "#1f2937",
    fontWeight: 500,
    fontSize: 13,
    lineHeight: 1.3,
  },
  body: {
    padding: "14px 16px 16px",
  },
  grid: {
    border: "1px solid #d1d5db",
    borderRadius: 12,
    overflow: "hidden",
    background: "#f4f6f8",
  },
  gridRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    borderBottom: "1px solid #d1d5db",
  },
  gridRowLast: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },
  gridCellLeft: {
    padding: "10px 14px",
    borderRight: "1px solid #d1d5db",
  },
  gridCellRight: {
    padding: "10px 14px",
  },
  gridCellFull: {
    padding: "10px 14px",
  },
  gridLabel: {
    fontSize: "0.6rem",
    color: "#828a96",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4,
  },
  gridValue: {
    color: "#1f2937",
    fontWeight: 500,
    fontSize: 13,
    lineHeight: 1.4,
  },
  actionBtn: {
    marginTop: 12,
    width: "100%",
    background: "#2b77d2",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "10px 0",
    fontWeight: 500,
    fontSize: 14,
    cursor: "pointer",
    letterSpacing: "0.03em",
    display: "block",
    textAlign: "center",
    boxSizing: "border-box",
  },
};

export default function EventPeek({ ev, onEdit, onDelete }) {
  const navigate = useNavigate();
  const fullUrl = ev?.url || null;

  const onGoDetails = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!fullUrl) return;
      if (typeof fullUrl === "string" && fullUrl.startsWith("/")) {
        navigate(fullUrl);
        return;
      }
      window.location.href = fullUrl;
    },
    [fullUrl, navigate]
  );

  if (!ev) return null;

  // source_table decide si mostrar la grilla del body (expediente/monto/forma de pago).
  // Los pagos de convenio de tribunal (pago único / parcialidad) usan la misma grilla que
  // los de centro de conciliación; no traen "Forma:" en la descripción (tribunal no la
  // captura), así que esa celda cae en "—" — el resto de los datos sí están disponibles.
  const descLowerForGrid = (ev.description || "").toLowerCase();
  const isPagoConvenio =
    ev.source_table === "conciliacion_audiencia" ||
    /^pago\s+de\s+convenio/i.test(ev.title || "") ||
    (ev.source_table === "tribunal_terminacion" &&
      (descLowerForGrid.includes("pago único del convenio") ||
        descLowerForGrid.includes("pago unico del convenio") ||
        descLowerForGrid.includes("pago parcialidad")));
  const convenio = isPagoConvenio ? parseConvenioDesc(ev.description) : null;
  // El label del header siempre se deriva del título
  let { typeLabel, namesLabel } = parseEventTitle(ev.title);
  // Limpia el "VS NONE" que quedó en títulos viejos guardados antes del fix de empresa vacía.
  namesLabel = namesLabel.replace(/\s*VS\s*NONE\.?\s*$/i, "").trim();
  // Todo evento de tribunal (términos, conciliatoria, preliminar, de juicio) usa la misma
  // regla de nombre: "Actora vs Razón social", reconstruida desde la descripción cuando el
  // título es un formato viejo (ver getTribunalDisplayName en boardUtils.jsx). El label de
  // arriba (typeLabel) se resuelve por source_table cuando es uno de los conocidos; si no
  // (registros viejos guardados bajo el source_table genérico "tribunal"), se saca de la
  // 1a línea de la descripción, que es estable para los 6 subtipos.
  const TRIBUNAL_ETAPA_LABELS = {
    tribunal_conciliatoria: "AUDIENCIA CONCILIATORIA",
    tribunal_preliminar: "AUDIENCIA PRELIMINAR",
    tribunal_audiencia_11: "AUDIENCIA PRELIMINAR",
    tribunal_audiencia_12: "AUDIENCIA DE JUICIO",
  };
  if ((ev.source_table || "").startsWith("tribunal")) {
    if (TRIBUNAL_ETAPA_LABELS[ev.source_table]) {
      typeLabel = TRIBUNAL_ETAPA_LABELS[ev.source_table];
    } else {
      const descLower = (ev.description || "").toLowerCase();
      if (
        descLower.includes("límite de contestación") ||
        descLower.includes("limite de contestacion") ||
        descLower.includes("presentación de demanda") ||
        descLower.includes("presentacion de demanda") ||
        descLower.includes("notificación de demanda") ||
        descLower.includes("notificacion de demanda")
      ) {
        // Cualquier "término" de tribunal (límite de contestación, presentación o
        // notificación de demanda) se muestra igual en el título gris.
        typeLabel = "TÉRMINO CONTESTACIÓN";
      } else if (descLower.includes("audiencia conciliatoria") || descLower.includes("conciliadora")) {
        typeLabel = "AUDIENCIA CONCILIATORIA";
      } else if (descLower.includes("audiencia preliminar")) {
        typeLabel = "AUDIENCIA PRELIMINAR";
      } else if (descLower.includes("audiencia") && descLower.includes("juicio")) {
        typeLabel = "AUDIENCIA DE JUICIO";
      } else if (descLower.includes("pago único del convenio") || descLower.includes("pago unico del convenio")) {
        typeLabel = "PAGO DE CONVENIO";
      } else if (descLower.includes("pago parcialidad") || descLower.includes("parcialidad")) {
        typeLabel = "PARCIALIDAD DE CONVENIO";
      } else if (descLower.includes("terminación del juicio por convenio") || descLower.includes("terminacion del juicio por convenio")) {
        typeLabel = "TERMINACIÓN POR CONVENIO";
      } else if (descLower.includes("constancia de cumplimiento")) {
        typeLabel = "CONSTANCIA DE CUMPLIMIENTO";
      }
    }
    namesLabel = getTribunalDisplayName(ev).toUpperCase();
  }

  return (
    <div style={S.card}>
      {/* ── Header ── */}
      <div style={S.header}>
        <div style={{ flex: 1, overflow: "hidden" }}>
          {typeLabel ? <div style={S.typeLabel}>{typeLabel}</div> : null}
          <h2 style={S.title}>{namesLabel}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button style={S.btnEdit} onClick={() => onEdit?.(ev)} title="Editar">
            <EditOutlined />
          </button>
          <button style={S.btnDelete} onClick={() => onDelete?.(ev)} title="Eliminar">
            <DeleteOutlined />
          </button>
        </div>
      </div>

      {/* ── Info bar: Fecha | Hora | Ubicación ── */}
      <div style={S.infoBar}>
        <div style={S.infoCol}>
          <div style={S.iconBox}>
            <CalendarOutlined />
          </div>
          <div>
            <div style={S.infoLabel}>Fecha</div>
            <div style={S.infoValue}>{fmtDate(ev)}</div>
          </div>
        </div>

        <div style={S.infoColBorder}>
          <div style={S.iconPlain}>
            <ClockCircleOutlined />
          </div>
          <div>
            <div style={S.infoLabel}>Hora</div>
            <div style={S.infoValue}>{fmtTime(ev)}</div>
          </div>
        </div>

        <div style={S.infoColBorder}>
          <div style={S.iconPlain}>
            <EnvironmentOutlined />
          </div>
          <div>
            <div style={S.infoLabel}>Ubicación</div>
            <div style={S.infoValue}>{ev.location || "—"}</div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={S.body}>
        {isPagoConvenio ? (
          /* Grilla estructurada para Pago de Convenio */
          <div style={S.grid}>
            <div style={S.gridRow}>
              <div style={S.gridCellLeft}>
                <div style={S.gridLabel}>Expediente</div>
                <div style={S.gridValue}>{convenio.expediente || "—"}</div>
              </div>
              <div style={S.gridCellRight}>
                <div style={S.gridLabel}>Autoridad</div>
                <div style={S.gridValue}>{ev.location || "—"}</div>
              </div>
            </div>
            <div style={S.gridRowLast}>
              <div style={S.gridCellLeft}>
                <div style={S.gridLabel}>Monto</div>
                <div style={S.gridValue}>{fmtMonto(convenio.monto)}</div>
              </div>
              <div style={S.gridCellRight}>
                <div style={S.gridLabel}>{convenio.forma ? "Forma de Pago" : "Tipo de Pago"}</div>
                <div style={S.gridValue}>{convenio.forma || convenio.tipoPago || "—"}</div>
              </div>
            </div>
          </div>
        ) : ev.description ? (
          /* Descripción genérica para los demás eventos */
          <div style={S.grid}>
            <div style={S.gridCellFull}>
              <div style={S.gridLabel}>Descripción</div>
              <div
                style={{ ...S.gridValue, maxHeight: 90, overflowY: "auto", lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: ev.description }}
              />
            </div>
          </div>
        ) : null}

        {fullUrl ? (
          <button style={S.actionBtn} onClick={onGoDetails}>
            IR AL EXPEDIENTE
          </button>
        ) : null}
      </div>
    </div>
  );
}
